import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { 
    Settings, CalendarDays, Users, User, LogOut, Menu, X, 
    Calendar, ClipboardList, Gift, History, Palette, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

// Import admin section components
import { AdminActivitiesSection } from '../components/admin/AdminActivitiesSection';
import { AdminAnalyticsSection } from '../components/admin/AdminAnalyticsSection';
import { AdminEventsSection } from '../components/admin/AdminEventsSection';
import { AdminOffersSection } from '../components/admin/AdminOffersSection';
import { AdminMembersSection } from '../components/admin/AdminMembersSection';
import { AdminBookingsSection } from '../components/admin/AdminBookingsSection';
import { AdminManagersSection } from '../components/admin/AdminManagersSection';
import { AdminActivityLogSection } from '../components/admin/AdminActivityLogSection';
import { AdminSettingsSection } from '../components/admin/AdminSettingsSection';
import { AdminBrandingSection } from '../components/admin/AdminBrandingSection';

// Import modal components
import {
    ActivityModal,
    EventModal,
    OfferModal,
    CreditModal,
    DeleteConfirmModal,
    AttendeesModal,
    EmailFacilitatorModal,
    BulkEmailModal,
    ManagerModal
} from '../components/admin/AdminModals';

import { API, formatDateTime } from '../components/admin/adminConstants';

export function AdminPage() {
    const navigate = useNavigate();
    const { token, logout, user } = useAuth();
    const { organization, refreshOrganization } = useOrganization();
    const [activeSection, setActiveSection] = useState('activities');
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    
    // Data states
    const [settings, setSettings] = useState({});
    const [activities, setActivities] = useState([]);
    const [members, setMembers] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [events, setEvents] = useState([]);
    const [offers, setOffers] = useState([]);
    const [managers, setManagers] = useState([]);
    const [activityLog, setActivityLog] = useState([]);
    const [orgSettings, setOrgSettings] = useState(null);
    
    // Modal states
    const [activityModal, setActivityModal] = useState({ open: false, data: null });
    const [eventModal, setEventModal] = useState({ open: false, data: null });
    const [offerModal, setOfferModal] = useState({ open: false, data: null });
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: '', id: null, name: '' });
    const [creditModal, setCreditModal] = useState({ open: false, member: null, amount: '', reason: '' });
    const [attendeesModal, setAttendeesModal] = useState({ open: false, activity: null, attendees: [], loading: false });
    const [emailFacilitatorModal, setEmailFacilitatorModal] = useState({ open: false, email: '', sending: false });
    const [bulkEmailModal, setBulkEmailModal] = useState({ open: false, subject: '', message: '', sending: false });
    const [managerModal, setManagerModal] = useState({ open: false, data: null });
    const [uploadingImage, setUploadingImage] = useState(false);

    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        setLoading(true);
        try {
            const [settingsRes, activitiesRes, membersRes, bookingsRes, eventsRes, offersRes, managersRes, activityLogRes] = await Promise.all([
                axios.get(`${API}/admin/settings`, { headers }),
                axios.get(`${API}/admin/activities`, { headers }),
                axios.get(`${API}/admin/members`, { headers }),
                axios.get(`${API}/admin/bookings`, { headers }),
                axios.get(`${API}/admin/events`, { headers }),
                axios.get(`${API}/admin/offers`, { headers }),
                axios.get(`${API}/admin/managers`, { headers }),
                axios.get(`${API}/admin/manager-activity-log`, { headers }),
            ]);
            
            setSettings(settingsRes.data);
            setActivities(activitiesRes.data);
            setMembers(membersRes.data);
            setBookings(bookingsRes.data);
            setEvents(eventsRes.data);
            setOffers(offersRes.data);
            setManagers(managersRes.data);
            setActivityLog(activityLogRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    // Settings handlers
    const handleSaveSettings = async () => {
        try {
            await axios.put(`${API}/admin/settings`, settings, { headers });
            toast.success('Settings saved');
        } catch (error) {
            toast.error('Failed to save settings');
        }
    };

    const handleToggleDemoMode = async () => {
        const newSettings = { ...settings, demo_mode: !settings.demo_mode };
        setSettings(newSettings);
        try {
            await axios.put(`${API}/admin/settings`, newSettings, { headers });
            toast.success(`Demo mode ${newSettings.demo_mode ? 'enabled' : 'disabled'}`);
        } catch (error) {
            toast.error('Failed to update demo mode');
        }
    };

    // Activity handlers
    const handleSaveActivity = async () => {
        const data = activityModal.data;
        if (!data.title || !data.description || !data.category || !data.date_time) {
            toast.error('Please fill in all required fields');
            return;
        }

        const isNew = !data.id;
        const endpoint = isNew ? `${API}/admin/activities` : `${API}/admin/activities/${data.id}`;
        const method = isNew ? 'post' : 'put';
        
        try {
            await axios[method](endpoint, data, { headers });
            toast.success(`Activity ${isNew ? 'created' : 'updated'}`);
            setActivityModal({ open: false, data: null });
            fetchAllData();
        } catch (error) {
            toast.error(`Failed to save activity`);
        }
    };

    const handleDeleteActivity = async (id) => {
        try {
            await axios.delete(`${API}/admin/activities/${id}`, { headers });
            toast.success('Activity deleted');
            setDeleteConfirm({ open: false, type: '', id: null, name: '' });
            fetchAllData();
        } catch (error) {
            toast.error('Failed to delete activity');
        }
    };

    const openNewActivityModal = () => {
        setActivityModal({
            open: true,
            data: {
                title: '',
                description: '',
                category: 'move_well',
                credit_cost: 1,
                capacity: 20,
                date_time: '',
                end_time: '',
                location: 'Village Hall',
                facilitator: '',
                is_free: false,
                image_url: ''
            }
        });
    };

    // Event handlers
    const handleSaveEvent = async () => {
        const data = eventModal.data;
        if (!data.title || !data.description || !data.date_time) {
            toast.error('Please fill in all required fields');
            return;
        }

        const isNew = !data.id;
        const endpoint = isNew ? `${API}/admin/events` : `${API}/admin/events/${data.id}`;
        const method = isNew ? 'post' : 'put';
        
        try {
            await axios[method](endpoint, data, { headers });
            toast.success(`Event ${isNew ? 'created' : 'updated'}`);
            setEventModal({ open: false, data: null });
            fetchAllData();
        } catch (error) {
            toast.error(`Failed to save event`);
        }
    };

    const handleDeleteEvent = async (id) => {
        try {
            await axios.delete(`${API}/admin/events/${id}`, { headers });
            toast.success('Event deleted');
            setDeleteConfirm({ open: false, type: '', id: null, name: '' });
            fetchAllData();
        } catch (error) {
            toast.error('Failed to delete event');
        }
    };

    const openNewEventModal = () => {
        setEventModal({
            open: true,
            data: {
                title: '',
                description: '',
                category: 'general',
                date_time: '',
                end_time: '',
                location: 'Burston Garden Centre',
                contact_phone: '01727 832444',
                contact_email: 'info@burston.co.uk',
                price: 'Free',
                image_url: '',
                booking_url: '',
                is_recurring: false,
                is_active: true
            }
        });
    };

    // Offer handlers
    const handleSaveOffer = async () => {
        const data = offerModal.data;
        if (!data.title || !data.description) {
            toast.error('Please fill in all required fields');
            return;
        }

        const isNew = !data.id;
        const endpoint = isNew ? `${API}/admin/offers` : `${API}/admin/offers/${data.id}`;
        const method = isNew ? 'post' : 'put';
        
        try {
            await axios[method](endpoint, data, { headers });
            toast.success(`Offer ${isNew ? 'created' : 'updated'}`);
            setOfferModal({ open: false, data: null });
            fetchAllData();
        } catch (error) {
            toast.error(`Failed to save offer`);
        }
    };

    const handleDeleteOffer = async (id) => {
        try {
            await axios.delete(`${API}/admin/offers/${id}`, { headers });
            toast.success('Offer deleted');
            setDeleteConfirm({ open: false, type: '', id: null, name: '' });
            fetchAllData();
        } catch (error) {
            toast.error('Failed to delete offer');
        }
    };

    const openNewOfferModal = () => {
        setOfferModal({
            open: true,
            data: {
                title: '',
                description: '',
                category: 'general',
                discount_type: 'percentage',
                discount_value: 0,
                valid_from: new Date().toISOString().slice(0, 10),
                valid_to: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
                terms_conditions: '',
                is_members_only: false,
                is_active: true
            }
        });
    };

    // Member handlers
    const handleAdjustCredits = async () => {
        if (!creditModal.member || !creditModal.amount || !creditModal.reason) {
            toast.error('Please fill in all fields');
            return;
        }
        try {
            await axios.put(
                `${API}/admin/members/${creditModal.member.id}/credits`,
                { 
                    user_id: creditModal.member.id,
                    amount: parseInt(creditModal.amount),
                    reason: creditModal.reason
                },
                { headers }
            );
            toast.success(`Credits adjusted for ${creditModal.member.name}`);
            setCreditModal({ open: false, member: null, amount: '', reason: '' });
            fetchAllData();
        } catch (error) {
            toast.error('Failed to adjust credits');
        }
    };

    const handleActivateMembership = async (memberId) => {
        try {
            await axios.put(
                `${API}/admin/members/${memberId}/membership`,
                { 
                    membership_status: 'active',
                    credits_balance: 30
                },
                { headers }
            );
            toast.success('Membership activated with 30 credits');
            fetchAllData();
        } catch (error) {
            toast.error('Failed to activate membership');
        }
    };

    const [createMemberLoading, setCreateMemberLoading] = useState(false);

    const handleCreateMember = async (memberData) => {
        setCreateMemberLoading(true);
        try {
            const response = await axios.post(`${API}/admin/members`, memberData, { headers });
            const result = response.data;
            
            if (result.welcome_email_sent) {
                toast.success(`${result.member.name} added! Welcome email sent to ${result.member.email}`);
            } else {
                toast.success(`${result.member.name} added! Temp password: ${result.temp_password}`);
            }
            
            fetchAllData();
            return true;
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create member');
            return false;
        } finally {
            setCreateMemberLoading(false);
        }
    };

    // Attendees handlers
    const handleViewAttendees = async (activity) => {
        setAttendeesModal({ open: true, activity, attendees: [], loading: true });
        try {
            const response = await axios.get(`${API}/activities/${activity.id}/attendees`, { headers });
            setAttendeesModal(prev => ({ ...prev, attendees: response.data, loading: false }));
        } catch (error) {
            console.error('Error fetching attendees:', error);
            toast.error('Failed to load attendees');
            setAttendeesModal(prev => ({ ...prev, loading: false }));
        }
    };

    const handleDownloadRegister = () => {
        const { activity, attendees } = attendeesModal;
        if (!activity || !attendees.length) return;

        const confirmed = attendees.filter(a => a.status === 'confirmed');
        const waitlist = attendees.filter(a => a.status === 'waitlist');

        let csv = 'CLASS REGISTER\n';
        csv += `Activity: ${activity.title}\n`;
        csv += `Date: ${formatDateTime(activity.date_time)}\n`;
        csv += `Location: ${activity.location}\n`;
        csv += `Facilitator: ${activity.facilitator || 'TBC'}\n`;
        csv += `Capacity: ${activity.capacity}\n`;
        csv += '\n';
        csv += 'CONFIRMED ATTENDEES\n';
        csv += 'No.,Name,Email,Membership No.,Booked Date,Attendance\n';
        
        confirmed.forEach((booking, index) => {
            const member = booking.member || {};
            csv += `${index + 1},"${member.name || 'Unknown'}","${member.email || ''}","${member.membership_number || ''}","${formatDateTime(booking.booked_at)}",\n`;
        });

        if (waitlist.length > 0) {
            csv += '\nWAITLIST\n';
            csv += 'No.,Name,Email,Membership No.,Joined Waitlist\n';
            waitlist.forEach((booking, index) => {
                const member = booking.member || {};
                csv += `${index + 1},"${member.name || 'Unknown'}","${member.email || ''}","${member.membership_number || ''}","${formatDateTime(booking.booked_at)}"\n`;
            });
        }

        csv += '\n---\n';
        csv += `Total Confirmed: ${confirmed.length}\n`;
        csv += `Waitlist: ${waitlist.length}\n`;
        csv += `Spaces Remaining: ${Math.max(0, activity.capacity - confirmed.length)}\n`;

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const filename = `register_${activity.title.replace(/[^a-z0-9]/gi, '_')}_${activity.date_time.split('T')[0]}.csv`;
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
        toast.success('Register downloaded');
    };

    const handlePrintRegister = () => {
        const { activity, attendees } = attendeesModal;
        if (!activity) return;

        const confirmed = attendees.filter(a => a.status === 'confirmed');
        const waitlist = attendees.filter(a => a.status === 'waitlist');

        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Class Register - ${activity.title}</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
                    .header { text-align: center; border-bottom: 2px solid #0F4D3F; padding-bottom: 15px; margin-bottom: 20px; }
                    .header h1 { color: #0F4D3F; margin: 0; }
                    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 20px; }
                    .info-item { padding: 8px; background: #f5f3ec; border-radius: 4px; }
                    .info-label { font-weight: bold; color: #666; font-size: 12px; }
                    table { width: 100%; border-collapse: collapse; margin: 15px 0; }
                    th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
                    th { background: #0F4D3F; color: white; }
                    tr:nth-child(even) { background: #f9f9f9; }
                    .attendance-box { width: 60px; height: 25px; border: 1px solid #999; }
                    h3 { color: #0F4D3F; margin-top: 25px; }
                    .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #ddd; font-size: 12px; color: #666; }
                    @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
                </style>
            </head>
            <body>
                <div class="header">
                    <h1>Class Register</h1>
                    <p style="color: #666;">Burston Residents Lifestyle Membership</p>
                </div>
                
                <div class="info-grid">
                    <div class="info-item"><span class="info-label">Activity</span><br/>${activity.title}</div>
                    <div class="info-item"><span class="info-label">Date & Time</span><br/>${formatDateTime(activity.date_time)}</div>
                    <div class="info-item"><span class="info-label">Location</span><br/>${activity.location}</div>
                    <div class="info-item"><span class="info-label">Facilitator</span><br/>${activity.facilitator || 'TBC'}</div>
                </div>
                
                <h3>Confirmed Attendees (${confirmed.length}/${activity.capacity})</h3>
                <table>
                    <tr>
                        <th style="width: 40px;">#</th>
                        <th>Name</th>
                        <th>Membership No.</th>
                        <th style="width: 80px;">Attendance</th>
                    </tr>
                    ${confirmed.map((booking, i) => `
                        <tr>
                            <td>${i + 1}</td>
                            <td>${booking.member?.name || 'Unknown'}</td>
                            <td>${booking.member?.membership_number || ''}</td>
                            <td><div class="attendance-box"></div></td>
                        </tr>
                    `).join('')}
                    ${confirmed.length === 0 ? '<tr><td colspan="4" style="text-align: center;">No confirmed attendees</td></tr>' : ''}
                </table>
                
                ${waitlist.length > 0 ? `
                    <h3>Waitlist (${waitlist.length})</h3>
                    <table>
                        <tr>
                            <th style="width: 40px;">#</th>
                            <th>Name</th>
                            <th>Membership No.</th>
                        </tr>
                        ${waitlist.map((booking, i) => `
                            <tr>
                                <td>${i + 1}</td>
                                <td>${booking.member?.name || 'Unknown'}</td>
                                <td>${booking.member?.membership_number || ''}</td>
                            </tr>
                        `).join('')}
                    </table>
                ` : ''}
                
                <div class="footer">
                    <p><strong>Spaces Remaining:</strong> ${Math.max(0, activity.capacity - confirmed.length)}</p>
                    <p>Printed: ${new Date().toLocaleString('en-GB')}</p>
                </div>
            </body>
            </html>
        `;

        const printWindow = window.open('', '_blank');
        if (printWindow) {
            printWindow.document.open();
            printWindow.document.write(printContent);
            printWindow.document.close();
            printWindow.focus();
            printWindow.print();
        }
    };

    const handleEmailToFacilitator = async () => {
        if (!emailFacilitatorModal.email) {
            toast.error('Please enter the facilitator email');
            return;
        }

        setEmailFacilitatorModal(prev => ({ ...prev, sending: true }));
        try {
            const response = await axios.post(`${API}/admin/email/register`, {
                activity_id: attendeesModal.activity.id,
                facilitator_email: emailFacilitatorModal.email
            }, { headers });
            
            toast.success(response.data.message);
            setEmailFacilitatorModal({ open: false, email: '', sending: false });
        } catch (error) {
            console.error('Error sending email:', error);
            toast.error(error.response?.data?.detail || 'Failed to send email');
            setEmailFacilitatorModal(prev => ({ ...prev, sending: false }));
        }
    };

    const handleBulkEmail = async () => {
        if (!bulkEmailModal.subject || !bulkEmailModal.message) {
            toast.error('Please fill in subject and message');
            return;
        }

        setBulkEmailModal(prev => ({ ...prev, sending: true }));
        try {
            const response = await axios.post(`${API}/admin/email/bulk`, {
                activity_id: attendeesModal.activity.id,
                subject: bulkEmailModal.subject,
                message: bulkEmailModal.message
            }, { headers });
            
            toast.success(response.data.message);
            setBulkEmailModal({ open: false, subject: '', message: '', sending: false });
        } catch (error) {
            console.error('Error sending bulk email:', error);
            toast.error(error.response?.data?.detail || 'Failed to send emails');
            setBulkEmailModal(prev => ({ ...prev, sending: false }));
        }
    };

    // Manager handlers
    const handleSaveManager = async () => {
        const data = managerModal.data;
        if (!data.email || !data.name || (!data.id && !data.password)) {
            toast.error('Please fill in all required fields');
            return;
        }

        const isNew = !data.id;
        const endpoint = isNew ? `${API}/admin/managers` : `${API}/admin/managers/${data.id}`;
        const method = isNew ? 'post' : 'put';
        
        const payload = isNew 
            ? { email: data.email, name: data.name, password: data.password }
            : { email: data.email, name: data.name, password: data.password || undefined };
        
        try {
            await axios[method](endpoint, payload, { headers });
            toast.success(`Manager ${isNew ? 'created' : 'updated'}`);
            setManagerModal({ open: false, data: null });
            fetchAllData();
        } catch (error) {
            toast.error(error.response?.data?.detail || `Failed to save manager`);
        }
    };

    const handleDeleteManager = async (id) => {
        try {
            await axios.delete(`${API}/admin/managers/${id}`, { headers });
            toast.success('Manager deleted');
            setDeleteConfirm({ open: false, type: '', id: null, name: '' });
            fetchAllData();
        } catch (error) {
            toast.error('Failed to delete manager');
        }
    };

    const openNewManagerModal = () => {
        setManagerModal({
            open: true,
            data: {
                email: '',
                name: '',
                password: ''
            }
        });
    };

    // Delete confirmation handler
    const handleDeleteConfirm = () => {
        if (deleteConfirm.type === 'activity') {
            handleDeleteActivity(deleteConfirm.id);
        } else if (deleteConfirm.type === 'event') {
            handleDeleteEvent(deleteConfirm.id);
        } else if (deleteConfirm.type === 'offer') {
            handleDeleteOffer(deleteConfirm.id);
        } else if (deleteConfirm.type === 'manager') {
            handleDeleteManager(deleteConfirm.id);
        }
    };

    // Navigation configuration
    const navSections = [
        {
            title: 'Overview',
            items: [
                { id: 'analytics', icon: BarChart3, label: 'Analytics' },
            ]
        },
        {
            title: 'Residents Club',
            items: [
                { id: 'activities', icon: Calendar, label: 'Activities' },
                { id: 'members', icon: User, label: 'Members' },
                { id: 'bookings', icon: ClipboardList, label: 'Bookings' },
            ]
        },
        {
            title: 'Public Content',
            items: [
                { id: 'events', icon: CalendarDays, label: 'Events' },
                { id: 'offers', icon: Gift, label: 'Offers' },
            ]
        },
        {
            title: 'Organization',
            items: [
                { id: 'branding', icon: Palette, label: 'Branding & Theme' },
            ]
        },
        {
            title: 'System',
            items: [
                { id: 'managers', icon: Users, label: 'Managers' },
                { id: 'activity-log', icon: History, label: 'Manager Activity' },
                { id: 'settings', icon: Settings, label: 'Settings' },
            ]
        }
    ];

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <div className="min-h-screen bg-background flex flex-col md:flex-row" data-testid="admin-page">
            {/* Mobile Header */}
            <div className="md:hidden sticky top-0 left-0 right-0 bg-primary z-40 p-4 flex items-center justify-between">
                <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white p-2" data-testid="mobile-menu-btn">
                    {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
                <h1 className="text-white font-bold">Admin Panel</h1>
                <button onClick={handleLogout} className="text-white p-2">
                    <LogOut className="w-5 h-5" />
                </button>
            </div>

            {/* Mobile Navigation Dropdown */}
            {sidebarOpen && (
                <div className="md:hidden bg-primary border-b border-white/10 p-4 z-30">
                    <nav className="space-y-4">
                        {navSections.map((section, idx) => (
                            <div key={section.title}>
                                <p className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-2">
                                    {section.title}
                                </p>
                                <div className="space-y-1">
                                    {section.items.map(item => (
                                        <button
                                            key={item.id}
                                            onClick={() => {
                                                setActiveSection(item.id);
                                                setSidebarOpen(false);
                                            }}
                                            className={`admin-nav-item w-full ${activeSection === item.id ? 'active' : ''}`}
                                            data-testid={`admin-nav-${item.id}`}
                                        >
                                            <item.icon className="w-5 h-5" />
                                            <span>{item.label}</span>
                                        </button>
                                    ))}
                                </div>
                                {idx < navSections.length - 1 && (
                                    <div className="border-b border-white/10 mt-3" />
                                )}
                            </div>
                        ))}
                    </nav>
                </div>
            )}

            {/* Desktop Sidebar */}
            <aside className="hidden md:flex md:flex-col md:w-64 md:min-h-screen bg-primary flex-shrink-0">
                <div className="p-4 border-b border-white/10">
                    <h1 className="text-xl font-bold text-white">Admin Panel</h1>
                    <p className="text-white/60 text-sm">{user?.email}</p>
                </div>
                
                <nav className="p-4 space-y-4 flex-1 overflow-y-auto">
                    {navSections.map((section, idx) => (
                        <div key={section.title}>
                            <p className="text-white/40 text-xs font-semibold uppercase tracking-wider mb-2 px-3">
                                {section.title}
                            </p>
                            <div className="space-y-1">
                                {section.items.map(item => (
                                    <button
                                        key={item.id}
                                        onClick={() => setActiveSection(item.id)}
                                        className={`admin-nav-item w-full ${activeSection === item.id ? 'active' : ''}`}
                                        data-testid={`admin-nav-desktop-${item.id}`}
                                    >
                                        <item.icon className="w-5 h-5" />
                                        <span>{item.label}</span>
                                    </button>
                                ))}
                            </div>
                            {idx < navSections.length - 1 && (
                                <div className="border-b border-white/10 mt-4" />
                            )}
                        </div>
                    ))}
                </nav>

                <div className="p-4 border-t border-white/10">
                    <button onClick={handleLogout} className="admin-nav-item w-full" data-testid="admin-logout-btn">
                        <LogOut className="w-5 h-5" />
                        <span>Logout</span>
                    </button>
                </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 p-4 md:p-8 overflow-auto">
                <div className="max-w-5xl mx-auto">
                    {loading ? (
                        <div className="flex justify-center py-20">
                            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : (
                        <>
                            {activeSection === 'analytics' && (
                                <AdminAnalyticsSection />
                            )}

                            {activeSection === 'activities' && (
                                <AdminActivitiesSection
                                    activities={activities}
                                    onOpenNewModal={openNewActivityModal}
                                    onEdit={(activity) => setActivityModal({ open: true, data: activity })}
                                    onDelete={(activity) => setDeleteConfirm({ open: true, type: 'activity', id: activity.id, name: activity.title })}
                                    onViewAttendees={handleViewAttendees}
                                />
                            )}

                            {activeSection === 'events' && (
                                <AdminEventsSection
                                    events={events}
                                    onOpenNewModal={openNewEventModal}
                                    onEdit={(event) => setEventModal({ open: true, data: event })}
                                    onDelete={(event) => setDeleteConfirm({ open: true, type: 'event', id: event.id, name: event.title })}
                                />
                            )}

                            {activeSection === 'offers' && (
                                <AdminOffersSection
                                    offers={offers}
                                    onOpenNewModal={openNewOfferModal}
                                    onEdit={(offer) => setOfferModal({ open: true, data: offer })}
                                    onDelete={(offer) => setDeleteConfirm({ open: true, type: 'offer', id: offer.id, name: offer.title })}
                                />
                            )}

                            {activeSection === 'members' && (
                                <AdminMembersSection
                                    members={members}
                                    onAdjustCredits={(member) => setCreditModal({ open: true, member, amount: '', reason: '' })}
                                    onActivateMembership={handleActivateMembership}
                                    onCreateMember={handleCreateMember}
                                    createLoading={createMemberLoading}
                                />
                            )}

                            {activeSection === 'bookings' && (
                                <AdminBookingsSection bookings={bookings} />
                            )}

                            {activeSection === 'managers' && (
                                <AdminManagersSection
                                    managers={managers}
                                    onOpenNewModal={openNewManagerModal}
                                    onEdit={(manager) => setManagerModal({ open: true, data: { ...manager, password: '' } })}
                                    onDelete={(manager) => setDeleteConfirm({ open: true, type: 'manager', id: manager.id, name: manager.name })}
                                />
                            )}

                            {activeSection === 'activity-log' && (
                                <AdminActivityLogSection
                                    activityLog={activityLog}
                                    onClearLog={async () => {
                                        try {
                                            await axios.delete(`${API}/admin/manager-activity-log`, { headers });
                                            toast.success('Activity log cleared');
                                            fetchAllData();
                                        } catch (error) {
                                            toast.error('Failed to clear activity log');
                                        }
                                    }}
                                />
                            )}

                            {activeSection === 'branding' && (
                                <AdminBrandingSection
                                    organization={organization}
                                    orgSettings={orgSettings}
                                    setOrgSettings={setOrgSettings}
                                    headers={headers}
                                    refreshOrganization={refreshOrganization}
                                />
                            )}

                            {activeSection === 'settings' && (
                                <AdminSettingsSection
                                    settings={settings}
                                    onSettingsChange={setSettings}
                                    onSaveSettings={handleSaveSettings}
                                    onToggleDemoMode={handleToggleDemoMode}
                                />
                            )}
                        </>
                    )}
                </div>
            </main>

            {/* Modals */}
            <ActivityModal
                modal={activityModal}
                setModal={setActivityModal}
                onSave={handleSaveActivity}
                headers={headers}
                uploadingImage={uploadingImage}
                setUploadingImage={setUploadingImage}
            />

            <EventModal
                modal={eventModal}
                setModal={setEventModal}
                onSave={handleSaveEvent}
                headers={headers}
                uploadingImage={uploadingImage}
                setUploadingImage={setUploadingImage}
            />

            <OfferModal
                modal={offerModal}
                setModal={setOfferModal}
                onSave={handleSaveOffer}
                headers={headers}
                uploadingImage={uploadingImage}
                setUploadingImage={setUploadingImage}
            />

            <CreditModal
                modal={creditModal}
                setModal={setCreditModal}
                onAdjust={handleAdjustCredits}
            />

            <DeleteConfirmModal
                modal={deleteConfirm}
                setModal={setDeleteConfirm}
                onConfirm={handleDeleteConfirm}
            />

            <AttendeesModal
                modal={attendeesModal}
                setModal={setAttendeesModal}
                onDownloadRegister={handleDownloadRegister}
                onPrintRegister={handlePrintRegister}
                onEmailFacilitator={() => setEmailFacilitatorModal({ open: true, email: '', sending: false })}
                onBulkEmail={() => setBulkEmailModal({ open: true, subject: `Update: ${attendeesModal.activity?.title || ''}`, message: '', sending: false })}
            />

            <EmailFacilitatorModal
                modal={emailFacilitatorModal}
                setModal={setEmailFacilitatorModal}
                onSend={handleEmailToFacilitator}
                activity={attendeesModal.activity}
            />

            <BulkEmailModal
                modal={bulkEmailModal}
                setModal={setBulkEmailModal}
                onSend={handleBulkEmail}
                activity={attendeesModal.activity}
                confirmedCount={attendeesModal.attendees.filter(a => a.status === 'confirmed').length}
            />

            <ManagerModal
                modal={managerModal}
                setModal={setManagerModal}
                onSave={handleSaveManager}
            />
        </div>
    );
}

export default AdminPage;
